// Square shortcut cards for Home: the three things Magi does that are NOT "type a topic".
//
// Each one lands on the Workspace with the composer already set to the matching mode, via
// `?mode=`. They deliberately do not open the create modal themselves — the composer is where
// these flows actually live, and jumping straight into a modal from another page hides the
// fact that the same thing is reachable by picking a mode, so nobody learns the app.
//
// `mode` values must exist in WorkspacePage's MODES. That page validates the parameter and
// silently falls back to Fast if it does not match, so a typo here degrades to "wrong mode
// preselected" rather than a broken page — quiet enough to be worth stating.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "../i18n/useT";
import { autoplayVideo } from "../utils/autoplayVideo";

// No icon badge: the animation already says what each card is, and a coloured tile pinned in
// the corner on top of it just read as decoration stuck on the artwork.
//
// THE POSTER IS A FRAME FROM THE MIDDLE OF THE LOOP, NOT ITS FIRST FRAME, and that distinction
// is the whole reason these cards looked broken. The poster exists because a blocked autoplay
// has to degrade to something — iOS refuses to autoplay in Low Power Mode however correctly the
// element is marked up, and any browser can refuse a play() that has nothing buffered yet.
// Without a poster the element paints nothing and the card is a black rectangle with a caption,
// which reads as a broken image. But these clips BUILD UP: every one of them opens on an almost
// empty field and fills in over the first few seconds. Posting the first frame therefore
// degraded to the emptiest moment in the clip — the Lecture card was a single orange book adrift
// in a dark rectangle (its poster was 4.5KB), the Course card was three loose dots — which does
// not read as a still, it reads as an image that failed to load. Reported exactly that way.
// The posters are now cut at t=8s of a 16s ping-ponged loop, which is the fullest the art ever
// gets, so a card that never animates still looks like the card.
const SHORTCUTS = [
  { mode: "hw", video: "/promo/card-hw.mp4", poster: "/promo/card-hw.jpg",
    titleKey: "home_sc_hw_title", descKey: "home_sc_hw_desc" },
  { mode: "lecture", video: "/promo/card-lecture.mp4", poster: "/promo/card-lecture.jpg",
    titleKey: "home_sc_lecture_title", descKey: "home_sc_lecture_desc" },
  { mode: "project", video: "/promo/card-course.mp4", poster: "/promo/card-course.jpg",
    titleKey: "home_sc_course_title", descKey: "home_sc_course_desc" },
];

const HomeShortcuts = ({ className = "" }) => {
  const navigate = useNavigate();
  const t = useT();

  return (
    <>
      {SHORTCUTS.map(({ mode, video, poster, titleKey, descKey }) => (
        <button
          key={mode}
          onClick={() => navigate(`/workspace?mode=${mode}`)}
          className={`group relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-black
            border border-white/10 hover:border-white/25 transition-colors text-left ${className}`}
        >
          {/* Same treatment as the Premium card: a ping-ponged Veo loop, silent, with
              autoPlay+muted+playsInline+loop together so it starts on its own on iOS. */}
          <video
            ref={autoplayVideo}
            src={video}
            poster={poster}
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
            tabIndex={-1}
          />
          {/* SHORT AND HARD, not a tall wash. The previous scrim ran 0.80 black at 40% height and
              still 0.20 at 68%, plus a 0.45 veil on the top edge — correct for the moody
              photographic footage these cards used to carry, and ruinous for flat vector art,
              which it turned to mud over most of the frame. The Kurzgesagt clips are already a
              dark midnight-blue field, so legibility only needs a compact band at the very
              bottom; everything above 52% is now left completely alone. Verified by compositing
              this exact gradient over real frames before changing it. */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 16%, rgba(0,0,0,0.30) 34%, rgba(0,0,0,0) 52%)",
            }}
          />

          <div className="absolute inset-0 flex flex-col justify-end p-6">
            <div>
              <p className="text-2xl font-bold leading-tight text-white mb-1.5">{t(titleKey)}</p>
              <p className="text-[14px] leading-snug text-white/60">{t(descKey)}</p>
            </div>
          </div>
        </button>
      ))}
    </>
  );
};

export default HomeShortcuts;
